import { toNumber } from 'lodash';
import {
  action,
  computed,
  configure,
  decorate,
  observable,
  reaction,
  runInAction,
} from 'mobx';
import UiMessageCodeHelpers from 'shared/utils/UiMessageCodeHelpers';
import { toBoolean } from 'stores/utils';
import mockdata from '../__mocks__/data/product_scratch.json';

configure({ enforceActions: 'always' });

export default class BidStore {
  constructor(AuctionStore, ProviderStore) {
    runInAction('BidStore', () => {
      this.auctionStore = AuctionStore;
      this.providerStore = ProviderStore;
      this.settings = mockdata.settings;
      this.auctionProduct = null;
      this.productStates = new Map();
      this.productStates.set(0, 'none');
      this.productStates.set(1, 'open');
      this.productStates.set(2, 'subjectTo');
      this.productStates.set(3, 'sold');
      this.productStates.set(4, 'noSale');
      // this.productStates.set(5, 'pulled');

      this.auction = observable({
        auctBAFontSize: toNumber(window.localStorage.getItem('auctBAFontSize')),
        auctioneerOpenPrivateNotes: { message: null, show: false },
        auctioneerOpenPublicComments: { message: null, show: false },
        blockStatus: null,
        chatFilter: { filterType: '', filterValue: '' },
        chatFontSize: toNumber(window.localStorage.getItem('chatFontSize')),
        confirmBadgeIdState: { buttonName: null, fix: false, valid: true },
        confirmDecisionClick: null,
        confirmingDecision: false,
        confirmModal: null,
        currentBid: null,
        defaultBidIncrement: this.auctionStore.getLaneSettingByProp('DefaultBidIncrement'),
        firstDecisionClick: null,
        flashBgColor: '#E7E7E7',
        flashBlockStatus: false,
        flashColor: '#000000',
        floorBidderBadge: null,
        focusOnBidButton: false,
        focusOnFloorBidderBadge: false,
        focusOnIndvMessage: false,
        focusOnLaneMessage: false,
        focusOnPinMessage: false,
        focusOnProductId: false,
        focusOnSidePanelSearch: false,
        focusOnStartingBidAmount: false,
        invokingEngine: false,
        isEditingFloorBidderBadge: false,
        isOpenForBidding: false,
        lastUIStateChangeTimestamp: null,
        lotNumber: null,
        monitorChatOnly: toBoolean(window.localStorage.getItem('monitorChatOnly')),
        nextBid: 0,
        onlineBidderHasBid: false,
        openItemNumberEntered: '',
        prevStatusBgColor: null,
        prevStatusColor: null,
        previousDecision: {
          decision: null, lotNumber: null, productState: null, winner: null,
        },
        previousBid: null,
        productID: null,
        productState: null,
        productStatused: false,
        restorePrevBlockStatusColors: null,
        selectFloorBidderBadge: false,
        selectedBidIncrementTabIndex: 1,
        sellerAccount: null,
        sellerAccountName: null,
        standardBidIncrement: null,
        startingBid: null,
        startingBidAmountEntered: null,
        statusBgColor: '#E7E7E7',
        statusColor: '#000000',
        tempBlockStatus: null,
        uiDecisionCode: 0,
        uiFocusCode: 0,
        uiModalFocusCode: 0,
        url: null,
        userActivityLog: [],
        winningBidderName: null,
        winningBidderNumber: null,
        zoomLevel: 1.0,
      });
    });

    reaction(
      () => this.auctionStore.laneStatus,
      () => this.handleLaneStatus(),
    );

    reaction(
      () => this.auction.currentBid,
      () => this.handleCurrentBidChange(),
    );

    reaction(
      () => this.auction.productState,
      () => this.handleProductStateChange(),
    );
  }

  handleUiMessageCodes = (data) => {
    try {
      const result = UiMessageCodeHelpers.getUiStateResult(data);
      if (result) {
        const { key, type, value } = result;
        // console.log('handleUiMessageCodes', { key, type, value })
        if (type === 'focus') {
          this.setProperty('uiFocusCode', value);
        } else if (type === 'modalFocus') {
          this.setProperty('uiModalFocusCode', value);
        } else if (type === 'decision') {
          this.setProperty('uiDecisionCode', value);
          if (['Sold', 'SubjectTo', 'NoSale'].includes(key)) {
            this.setProperty('firstDecisionClick', key);
          }
        } else if (type === 'debugging') {
          this.setProperty('debugging', key);
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  handleLaneStatus = () => {
    if (this.auctionStore.laneStatus === 'Ended') {
      this.initializeBidding();
    }
  };

  handleCurrentBidChange = () => {
    this.setProperty('flashBlockStatus', true);
  };

  backupBlockStatus = () => {
    const status = this.auction.blockStatus.split(' (code:')[0].trim();
    this.setProperty('tempBlockStatus', status);
  };

  handleProductStateChange = () => {
    const productState = this.productStates.get(this.auction.productState);
    if (productState) {
      if (productState === 'none') {
        this.setProperty('nextBid', null);
      }
      const ignoreStates = ['none', 'open'];
      const productStatused = ignoreStates.indexOf(productState) === -1;
      this.setProperty('productStatused', productStatused);
    } else {
      this.setProperty('productStatused', false);
    }
  };

  initializeBidding = (isReRun = false) => {
    console.log('initializeBidding')
    this.auctionProduct = null;
    this.setProperty('confirmBadgeIdState', {
      buttonName: null,
      fix: false,
      valid: true,
    });
    if (!isReRun) {
      this.setProperty('lotNumber', null);
      this.setProperty('productID', null);
      this.setProperty('sellerAccount', null);
      this.setProperty('sellerAccountName', null);
    }
    this.setProperty('confirmDecisionClick', null);
    this.setProperty('confirmingDecision', false);
    this.setProperty('currentBid', 0);
    this.setProperty('defaultBidIncrement', this.providerStore.getProviderSettingByKey('DefaultBidIncrement'));
    this.setProperty('firstDecisionClick', null);
    this.setProperty('flashBlockStatus', false);
    this.setProperty('floorBidderBadge', null);
    this.setProperty('focusOnBidButton', false);
    this.setProperty('focusOnFloorBidderBadge', false);
    this.setProperty('focusOnStartingBidAmount', !toBoolean(window.localStorage.getItem('monitorChatOnly')));
    this.setProperty('focusOnProductId', false);
    this.setProperty('focusOnIndvMessage', false);
    this.setProperty('focusOnLaneMessage', false);
    this.setProperty('focusOnPinMessage', false);
    this.setProperty('isEditingFloorBidderBadge', false);
    this.setProperty('isOpenForBidding', false);
    this.setProperty('isOpenForBiddingFromStartingBidAmount', false);
    this.setProperty('nextBid', 0);
    this.setProperty('onlineBidderHasBid', false);
    this.setProperty('openItemNumberEntered', '');
    this.setProperty('productState', null);
    this.setProperty('productStatused', false);
    this.setProperty('selectFloorBidderBadge', false);
    this.setProperty('standardBidIncrement', this.auction.defaultBidIncrement);
    this.setProperty('startingBid', null);
    this.setProperty('startingBidAmountEntered', null);
    this.setProperty('statusBgColor', '#E7E7E7');
    this.setProperty('statusColor', '#000');
    this.setProperty('flashBgColor', '#E7e7e7');
    this.setProperty('flashColor', '#000');
    this.setProperty('tempBlockStatus', null);
    this.setProperty('uiDecisionCode', 0);
    this.setProperty('uiFocusCode', 0);
    this.setProperty('url', null);
    this.setProperty('userActivityLog', []);
    this.setProperty('winningBidderName', null);
    this.setProperty('winningBidderNumber', null);
    this.setProperty('monitorChatOnly', toBoolean(window.localStorage.getItem('monitorChatOnly')));
    this.setProperty('invokingEngine', false);
  };

  setStartingBidAmountEntered = (value) => {
    this.setProperty('startingBidAmountEntered', value);
  };

  setOpenItemNumberEntered = (value) => {
    this.setProperty('openItemNumberEntered', value);
  };

  isNoSaleClick = () => this.auction.firstDecisionClick === 'NoSale';

  isFirstClickNoSale = () => this.firstDecisionClick === 'NoSale';

  isConfirmingNoSaleClick = () => this.isConfirmingDecision && this.confirmDecisionClick === 'NoSale';

  isConfirmingSoldClick = () => this.isConfirmingDecision && this.confirmDecisionClick === 'Sold';

  isConfirmingIfSaleClick = () => (
    this.isConfirmingDecision && this.confirmDecisionClick === 'SubjectTo'
  );

  hasNotDecisionButtonClicked = () => !this.auction.firstDecisionClick;

  get laneSuspended() {
    return this.auctionStore.laneStatus === 'Suspended';
  }

  get lanePaused() {
    return this.auctionStore.laneStatus === 'Paused';
  }

  isConnected = () => this.auctionStore.connected;

  isNotConnected = () => !this.auctionStore.connected;

  isNotOpenForBidding = () => !this.auction.isOpenForBidding;

  isNotFloorBidderBid = () => !this.isFloorBidderBid;

  isConfirmingSoldOrIfSale = () => this.isConfirmingDecision && !this.isNoSaleClick();

  floorBidderBadgeIsEmpty = () => !this.hasEnteredFloorBidderBadge();

  hasEnteredFloorBidderBadge = () => (
    !!this.auction.floorBidderBadge && this.auction.floorBidderBadge !== ''
  );

  isConfirmingSoldOrIfSale = () => this.isConfirmingSoldClick() || this.isConfirmingIfSaleClick();

  setFirstDecisionClick = (value) => {
    this.setConfirmingDecision(false);
    this.setProperty('firstDecisionClick', value);
  };

  setConfirmingDecisionClick = (value) => {
    this.setProperty('confirmDecisionClick', value);
  };

  setConfirmingDecision = (value) => {
    this.setProperty('confirmingDecision', value);
  };

  resetConfirmations = () => {
    this.setFirstDecisionClick(null);
    this.setConfirmingDecisionClick(null);
    this.setConfirmingDecision(false);
  };

  setConfirmBadgeIdState = (fix, valid, buttonName) => {
    this.setProperty('confirmBadgeIdState', {
      buttonName,
      fix,
      valid,
    });
  };

  setSelectFloorBidderBadge = (value) => {
    this.setProperty('selectFloorBidderBadge', value);
  };

  get initialClickIsNoSale() {
    return this.isFirstClickNoSale();
  }

  get floorBidderBadgeConfirmationIsValid() {
    return this.auction.confirmBadgeIdState.valid;
  }

  get isFixingInvalidFBB() {
    return this.auction.confirmBadgeIdState.fix;
  }

  get invalidConfirmationButtonName() {
    return this.auction.confirmBadgeIdState.buttonName;
  }

  get shouldDisableDecisionButtons() {
    return this.disableAskingBid || !this.auction.currentBid || this.auctionStore.laneStatus === 'Paused' || this.auctionStore.laneStatus === 'Ended';
  }

  get shouldDisableBidButton() {
    const enableButton = !this.disableAskingBid && !this.laneSuspended;
    return !enableButton;
  }

  get shouldDisableRetractBidButton() {
    return this.disableAskingBid || !this.auction.currentBid;
  }

  get shouldDisableFloorBidderOverrideButton() {
    return this.disableAskingBid || !this.auction.currentBid || this.isFloorBidderBid;
  }

  get shouldDisableStartingBidAmount() {
    const enable = this.auctionStore.connected
      && this.auctionStore.runList.length > 0
      && !this.auction.productStatused
      && !this.lanePaused
      && !this.laneSuspended;
    return !enable;
  }

  get shouldDisableOpenItem() {
    return (
      this.isNotConnected()
      || !this.auctionStore.auctionLaneId
      || this.auction.isOpenForBidding
      || this.auctionStore.runList.length === 0
      || this.auctionStore.lanePaused
      || this.auctionStore.laneSuspended
    );
  }

  get showIfSaleButton() {
    const { buttonName, fix } = this.auction.confirmBadgeIdState;
    return !buttonName || buttonName !== 'SubjectTo' || !fix;
  }

  get showSoldButton() {
    const { buttonName, fix } = this.auction.confirmBadgeIdState;
    return !buttonName || buttonName !== 'Sold' || !fix;
  }

  get showNoSaleButton() {
    return true;
  }

  get isConfirmingDecision() {
    return this.laneSuspended;
  }

  get noFloorBidderBadgeEntered() {
    return (
      !this.auction.floorBidderBadge || this.auction.floorBidderBadge === ''
    );
  }

  get isFloorBidderBid() {
    return (
      this.auction.isOpenForBidding
      && this.auction.winningBidderName === 'FLOOR BIDDER'
    );
  }

  enableAskingBidAmountFlashing = () => {
    const doFlash = this.auction.isOpenForBidding
      && this.auction.currentBid > 0
      && this.auction.flashBlockStatus;

    this.setProperty('flashBlockStatus', false);

    return doFlash;
  };

  get disableAskingBid() {
    return !this.auctionStore.connected || !this.auction.isOpenForBidding || this.auctionStore.laneStatus === 'Paused' || this.auctionStore.laneStatus === 'Ended';
  }

  setUserActivityLog = (message) => {
    const log = {
      auctionData: this.auction,
      auctions: this.auctionList,
      bid: {
        bidIncrement: this.auction.bidIncrement,
        nextBid: this.auction.nextBid,
        winningBidAmount: this.auction.winningBidAmount,
        winningBidderName: this.auction.winningBidderName,
        winningBidderNumber: this.auction.winningBidderNumber,
      },
      currentAuction: this.auction.auctionName,
      currentLane: this.auction.auctionLaneName,
      floorBidderBadgeIsEmpty: this.floorBidderBadgeIsEmpty(),
      floorBidderBadgeIsInvalid: this.floorBidderBadgeIsInvalid,
      getFloorBidderBadgePlaceholderText: this
        .getFloorBidderBadgePlaceholderText,
      hasEnteredFloorBidderBadge: this.hasEnteredFloorBidderBadge(),
      hasNotDecisionButtonClicked: this.hasNotDecisionButtonClicked(),
      isConfirmingDecision: this.isConfirmingDecision,
      isConfirmingNoSaleClick: this.isConfirmingNoSaleClick(),
      isConfirmingSoldClick: this.isConfirmingSoldClick(),
      isConfirmingSoldOrIfSale: this.isConfirmingSoldOrIfSale(),
      isFloorBidderBid: this.isFloorBidderBid,
      isNoSaleClick: this.isNoSaleClick(),
      isNotConnected: this.isNotConnected(),
      isNotFloorBidderBid: this.isNotFloorBidderBid(),
      isNotOpenForBidding: this.isNotOpenForBidding(),
      lanes: this.AuctionLaneList,
      lotNumber: this.auction.lotNumber,
      message,
      noFloorBidderBadgeEntered: this.noFloorBidderBadgeEntered,
      product: this.auctionProduct,
      runList: this.AuctionRunList,
      shouldDisableFloorBidderBadge: this.shouldDisableFloorBidderBadge,
      time: new Date().toISOString(),
      user: this.user,
    };
    this.auction.userActivityLog.push(log);
  };

  resetUserActivityLog = () => {
    this.setProperty('userActivityLog', []);
  };

  showUserActivityLog = () => {
    const messages = [];
    const bids = [];
    this.auction.userActivityLog.map((item) => {
      if (item?.message) {
        messages.push(item.message);
      }
      bids.push(item.bid);
      return true;
    });
    this.auction.userActivityLog = [];
  };

  get shouldDisableResumeButton() {
    const enableButton = (this.auction.productStatused || this.laneSuspended) && !this.auction.invokingEngine;
    return !enableButton;
  }

  get shouldDisableReRunButton() {
    if (this.auction.invokingEngine) return true;
    const enableButton = (this.auctionStore.laneStatus === 'Running'
        && this.auction.product !== ''
        && this.auction.currentBid > 0)
        || this.laneSuspended
      || this.auction.productStatused;
    return !enableButton;
  }

  get shouldDisableStartingAmountButton() {
    const startingBidAmountEnteredAsNumber = Number(this.auction.startingBidAmountEntered?.replace(/[\\$,\s]/g, ''));
    const enableButton = this.auctionStore.connected
      && !!this.auction.startingBidAmountEntered
      && startingBidAmountEnteredAsNumber > this.auction.currentBid
      && !this.lanePaused
      && !this.laneSuspended
      && !this.isConfirmingDecision;

    return !enableButton;
  }

  get floorBidderBadgeIsInvalid() {
    const requireBidderNumber = parseInt(
      this.providerStore.getProviderSettingByKey('RequireBidderNumber'),
    ) > 0;

    if (!requireBidderNumber) {
      return false;
    }

    return (
      this.isFloorBidderBid
      && this.noFloorBidderBadgeEntered
      && this.auction.firstDecisionClick !== 'NoSale'
    );
  }

  get shouldDisableSoldOrIfSaleButtons() {
    return (
      !this.auctionStore.connected
      || !this.auction.isOpenForBidding
      || this.auction.winningBidderName === ''
      || (this.auction.confirmingDecision
        && this.auction.winningBidderName === 'FLOOR BIDDER'
        && !this.auction.floorBidderBadge)
      || (this.auction.floorBidderBadge
        && this.auction.floorBidderBadge.length === 0)
    );
  }

  get shouldDisableFloorBidderBadge() {
    const requireBidderNumber = parseInt(
      this.providerStore.getProviderSettingByKey('RequireBidderNumber'),
    ) > 0;

    if (!requireBidderNumber) {
      return true;
    }

    const enable = !this.isNotConnected()
      && !this.isNotOpenForBidding()
      && !this.isNotFloorBidderBid()
      && this.laneSuspended;
    return !enable;
  }

  get getFloorBidderBadgePlaceholderText() {
    if (this.isConfirmingDecision && !this.shouldDisableFloorBidderBadge && this.floorBidderBadgeIsEmpty()) {
      if (this.isNoSaleClick()) {
        return '';
      }
      return 'required';
    }
    return '';
  }

  setProperty = (property, value) => runInAction(() => {
    this.auction[property] = value;
  });

  handleCloseBidding = (state) => {
    // TEMP: Until we get an product from the server with the new status
    this.auctionStore.updateRunListItemState(this.auction.lotNumber, state);
    this.initializeBidding();
  };

  setProduct = (data) => {
    this.auctionProduct = data;
    this.setProperty('productID', data.productID);
  };

  endItem = () => {
    this.auctionProduct = null;
    this.setProperty('lotNumber', null);
    this.setProperty('productID', null);
    this.setProperty('url', null);
    this.setProperty('startingBid', null);
    this.setProperty('onlineBidderHasBid', false);
  };

  updateBlock = ({
    bidIncrement,
    isOpenForBidding,
    lotNumber,
    nextBidAmount,
    product,
    productID,
    productState,
    sellerAccount,
    sellerAccountName,
    sellerAction,
    sellerActionAmount,
    startingBidAmount,
    url,
    winningBidAmount,
    winningBidderName,
    winningBidderNumber,
  }) => {
    this.setProperty('invokingEngine', false);
    if (lotNumber !== this.auction.lotNumber) {
      this.setProperty('sellerAccount', sellerAccount);
      this.setProperty('sellerAccountName', sellerAccountName);
      this.setProperty('sellerAction', sellerAction);
      this.setProperty('sellerActionAmount', sellerActionAmount);
    }
    this.setProperty('lotNumber', lotNumber);
    this.setProperty('currentBid', winningBidAmount);
    this.setProperty('winningBidderNumber', winningBidderNumber);
    this.setProperty('productState', productState);
    this.setProperty('product', product);
    this.setProperty('productID', productID);
    if (winningBidderName !== '') {
      this.setProperty('winningBidderName', winningBidderName);
    } else {
      this.setProperty('winningBidderName', null);
    }
    if (nextBidAmount && nextBidAmount !== this.auction.nextBid) {
      this.setProperty('nextBid', nextBidAmount);
    }
    this.setProperty('startingBid', startingBidAmount);
    this.setProperty('isOpenForBidding', isOpenForBidding);
    this.setProperty('standardBidIncrement', bidIncrement);
    this.setProperty('url', url);
    this.setProperty(
      'onlineBidderHasBid',
      !!winningBidderName && winningBidderName !== 'FLOOR BIDDER',
    );
  };

  handleServerBlockMessage = (payload) => {
    const message = payload?.message;
    const type = payload?.type;

    // TODO: Move to shared theme
    const color = {
      black: '#000000',
      blue: '#0095E8',
      default: '#e7e7e7',
      green: '#21BA45',
      red: '#DB2828',
      white: '#ffffff',
      yellow: '#FBCC08',
    };

    // TODO: Move to ProviderStore
    const messageTypeBlockStatusMappings = [
      {
        bgColor: 'red',
        color: 'white',
        flash: {
          bgColor: 'red',
          color: 'white',
        },
        messageType: -1,
        messageTypeName: 'userError',
      },
      {
        bgColor: 'default',
        color: 'black',
        flash: {
          bgColor: 'default',
          color: 'black',
        },
        messageType: 0,
        messageTypeName: 'message',
      },
      {
        bgColor: 'blue',
        color: 'white',
        flash: {
          bgColor: 'default',
          color: 'black',
        },
        messageType: 1,
        messageTypeName: 'onlineBid',
      },
      {
        bgColor: 'default',
        color: 'black',
        flash: {
          bgColor: 'green',
          color: 'white',
        },
        messageType: 2,
        messageTypeName: 'floorBid',
      },
      {
        bgColor: 'default',
        color: 'black',
        flash: {
          bgColor: 'default',
          color: 'black',
        },
        messageType: 3,
        messageTypeName: 'laneStatusChange',
      },
      {
        bgColor: 'default',
        color: 'black',
        flash: {
          bgColor: 'default',
          color: 'black',
        },
        messageType: 4,
        messageTypeName: 'onBlockChanged',
      },
      {
        bgColor: 'red',
        color: 'white',
        flash: {
          bgColor: 'red',
          color: 'white',
        },
        messageType: 5,
        messageTypeName: 'noSale',
      },
      {
        bgColor: 'green',
        color: 'white',
        flash: {
          bgColor: 'green',
          color: 'white',
        },
        messageType: 6,
        messageTypeName: 'sold',
      },
      {
        bgColor: 'yellow',
        color: 'black',
        flash: {
          bgColor: 'yellow',
          color: 'black',
        },
        messageType: 7,
        messageTypeName: 'subjectTo',
      },
      {
        bgColor: 'default',
        color: 'black',
        flash: {
          bgColor: 'default',
          color: 'black',
        },
        messageType: 8,
        messageTypeName: 'info',
      },
    ];

    const setColorsForBlockStatus = (messageType) => {
      const mapping = messageTypeBlockStatusMappings.filter(
        (item) => item.messageType === messageType,
      )[0];
      runInAction('Set Block Status Color', () => {
        this.setProperty('flashBgColor', color[mapping.flash.bgColor]);
        this.setProperty('flashColor', color[mapping.flash.color]);
        this.setProperty('statusBgColor', color[mapping.bgColor]);
        this.setProperty('statusColor', color[mapping.color]);
      });
    };

    const getSkipLatestBlockStatusConditions = () => {
      // HACK: Currently, when we make a decision on item, lane is resumed after the next on block is message appears. When this happens, we want to ignore showing the lane resumed message and instead show the next on block message
      const currentlyNextOnBlockAndIncomingIsLaneResumed = this.auction.blockStatus?.indexOf('Next On Block') > -1
        && message?.indexOf('Resumed') > -1;

      //   const currentlyEndOfRunList =
      //     this.auction.blockStatus?.indexOf('Cleared OnBlock') > -1 &&
      //     message?.indexOf('Resumed') > -1;

      //   const currentlyLaneMediaStatus =
      //     this.auction.blockStatus?.indexOf('Cleared OnBlock') > -1 &&
      //     message?.indexOf('Lane Media') > -1;

      return [
        currentlyNextOnBlockAndIncomingIsLaneResumed,
        // currentlyEndOfRunList,
        // currentlyLaneMediaStatus,
      ];
    };

    const isTempBlockStatusMessage = (tempMessageType) => {
      //console.log('isTempBlockStatusMessage', this.auction.productState, this.auction.statusBgColor, this.auction)
      const showTemp = message.indexOf('Retract') > -1
      || message.indexOf('Increment') > -1;
      if (showTemp) {
        const prevStatusBgColor = this.auction.prevStatusBgColor;
        const prevStatusColor = this.auction.prevStatusColor;

        runInAction('Set Temp Block Status Color', () => {
          setColorsForBlockStatus(tempMessageType);
          this.setProperty('tempBlockStatus', message);
          setTimeout(() => {
            this.setProperty('tempBlockStatus', null);
            this.setProperty('restorePrevBlockStatusColors', null);
            this.setProperty('prevStatusBgColor', null);
            this.setProperty('prevStatusColor', null);
            if (message.indexOf('Increment') > -1) {
              this.setProperty('statusBgColor', prevStatusBgColor);
              this.setProperty('statusColor', prevStatusColor);
            }
          }, 1000);
        });

        return true;
      }

      setColorsForBlockStatus(tempMessageType);
      return false;
    };

    this.handleProductStateChange();

    const conditions = getSkipLatestBlockStatusConditions();
    const isFalse = (currentValue) => !currentValue;
    const continueWithBlockStatusUpdate = conditions.every(isFalse);

    // HACK: Hiding errors temporarily until we figure out this timing issue
    if (![-1, 0].includes(parseInt(type))) {
      setColorsForBlockStatus(type);
      if (parseInt(type) === 3 && this.auctionStore.laneStatus !== 'Running') {
        this.setProperty('blockStatus', message);
        this.setProperty('tempBlockStatus', null);
        this.setProperty('restorePrevBlockStatusColors', null);
      } else if (continueWithBlockStatusUpdate) {
        if (!isTempBlockStatusMessage(type)) {
          this.setProperty('blockStatus', message);
        }
      }
    }
  };
}

decorate(BidStore, {
  auction: observable,
  auctionProduct: observable,
  backupBlockStatus: action,
  chatFilter: observable,
  disableAskingBid: computed,
  enableAskingBidAmountFlashing: action,
  endItem: action,
  floorBidderBadgeConfirmationIsValid: computed,
  floorBidderBadgeIsInvalid: computed,
  getFloorBidderBadgePlaceholderText: computed,
  getLaneStatusChangeReasonKeyByValue: action,
  handleProductStateChange: action,
  handleServerBlockMessage: action,
  handleUiMessageCodes: action,
  initialClickIsNoSale: computed,
  initializeBidding: action,
  invalidConfirmationButtonName: computed,
  isConfirmingDecision: computed,
  isFixingInvalidFBB: computed,
  isFloorBidderBid: computed,
  isNoSaleClick: action,
  lanePaused: computed,
  laneSuspended: computed,
  noFloorBidderBadgeEntered: computed,
  productItem: observable,
  productStates: observable,
  resetConfirmations: action,
  resetUserActivityLog: action,
  setBlockStatus: action,
  setConfirmBadgeIdState: action,
  setConfirmingDecision: action,
  setConfirmingDecisionClick: action,
  setConnectecStatus: action,
  setFirstDecisionClick: action,
  setOpenItemNumberEntered: action,
  setProduct: action,
  setProperty: action,
  setStartingBidAmountEntered: action,
  setUiMessageCode: action,
  setUserActivityLog: action,
  shouldDisableBidButton: computed,
  shouldDisableDecisionButtons: computed,
  shouldDisableFloorBidderBadge: computed,
  shouldDisableFloorBidderOverrideButton: computed,
  shouldDisableOpenItem: computed,
  shouldDisableReRunButton: computed,
  shouldDisableResumeButton: computed,
  shouldDisableRetractBidButton: computed,
  shouldDisableSoldOrIfSaleButtons: computed,
  shouldDisableStartingAmountButton: computed,
  shouldDisableStartingBidAmount: computed,
  showIfSaleButton: computed,
  showSoldButton: computed,
  showUserActivityLog: action,
  uiLaneStatusMessageCodeResult: observable,
  uiMessageCode: observable,
  uiMessageCodes: observable,
  updateBlock: action,
});
